import { useTranslator } from '@bluebeam/react-i18n';
import { BannerContext } from 'components/Routes/BannerContext';
import SearchInputResults from 'components/inputs/SearchInputResults';
import { Button, Modal, ModalFooter } from 'components/lib';
import AppStoreContext from 'contexts/AppStoreContext';
import { isNil, noop } from 'lodash';
import React, { FC, useContext, useEffect, useState } from 'react';
import { GeoLocation, RawGeoLocation } from 'records/GeoLocation';
import { Project } from 'records/Project';
import { searchGeolocation } from 'services/mapService';
import { getProjectAddress } from 'utils/map';

type Props = {
  onCloseModal: () => void;
  project: Project;
};

const GeolocationModal: FC<Props> = ({ onCloseModal, project }: Props) => {
  const [isActive, setIsActive] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [results, setResults] = useState<Array<GeoLocation> | null>();
  const [selectedProjectLocation, setSelectedProjectLocation] =
    useState(Object);
  const [defaultValue, setDefaultValue] = useState(getProjectAddress(project));
  const [hasError, setError] = useState(false);
  const { actions } = useContext(AppStoreContext);
  const { height: bannerHeight } = useContext(BannerContext);
  const overlayTopOffset = `calc(48px + ${bannerHeight}px)`;
  const { translate } = useTranslator(['geolocationModal']);
  const handleSearchChange = async (searchString: string) => {
    try {
      setIsLoading(true);
      const emptyResults: GeoLocation[] = [];
      const searchLocations = await searchGeolocation(searchString);

      if (!isNil(searchLocations)) {
        setResults(searchLocations);
      } else {
        setResults(emptyResults);
      }

      setIsActive(false);
      setIsLoading(false);
    } catch (error) {
      setError(true);
    }
  };

  const handleSelect = (selectedLocation: RawGeoLocation) => {
    setSelectedProjectLocation(selectedLocation);
    setIsActive(true);
  };

  const handleSubmit = () => {
    actions.setProjectGeoLocation(selectedProjectLocation);
    onCloseModal();
  };

  useEffect(() => {
    handleSearchChange(`${defaultValue}`);
  }, [defaultValue]);

  return (
    <>
      <style>
        {`
          #user-circle-popover-overlay {z-index:1001 !important;}
        `}
      </style>
      <Modal
        title={translate('geolocationModal:title')}
        overlayStyle={{
          top: `${overlayTopOffset}`,
        }}
        size="medium"
        onDismiss={noop}
        showOverflow
        disableCloseBtn
      >
        <div
          style={{
            position: 'relative',
            margin: '2rem',
            height: '260px',
          }}
        >
          <p className="size-16 light">
            {translate('geolocationModal:selectAddress')}
          </p>
          <SearchInputResults
            id="project-location-search"
            placeholder="Search"
            defaultValue={defaultValue}
            onChange={handleSearchChange}
            onSelect={handleSelect}
            results={results}
            isLoading={isLoading}
            hasError={hasError}
            style={{
              height: '40px',
              padding: '8px',
              fontSize: '16px',
            }}
          />
        </div>
        <ModalFooter>
          <Button
            variant="primary"
            size="large"
            onClick={handleSubmit}
            type="submit"
            disabled={!isActive}
          >
            {translate('geolocationModal:modalSubmit')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};
export default GeolocationModal;
